const cache = new Map();

const cacheKey = (shipmentId, outcomeId) =>
  `${String(shipmentId)}:${String(outcomeId)}`;

export const getCachedScoringExplanation = (shipmentId, outcomeId) => {
  if (shipmentId == null || outcomeId == null || outcomeId === "") return null;
  return cache.get(cacheKey(shipmentId, outcomeId)) ?? null;
};

export const setCachedScoringExplanation = (shipmentId, outcomeId, data) => {
  if (shipmentId == null || outcomeId == null || outcomeId === "") return;
  cache.set(cacheKey(shipmentId, outcomeId), data);
};

export const clearScoringExplanationCache = () => {
  cache.clear();
};

/** Drop cached explanation for an outcome (all carrier-specific scopes). */
export const invalidateScoringExplanationCache = (shipmentId, outcomeId) => {
  if (shipmentId == null || outcomeId == null || outcomeId === "") return;
  const prefix = `${String(shipmentId)}:${String(outcomeId)}`;
  for (const key of [...cache.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}:`)) {
      cache.delete(key);
    }
  }
};

const normalizeCarrierCode = (code) =>
  (code || "").toString().trim().toUpperCase();

const KPI_FACTOR_KEY = /^kpi[_-]?factor(s)?$/i;

const isKpiFactorKey = (key) =>
  KPI_FACTOR_KEY.test(String(key || "").trim());

/** Unwrap common API envelope shapes. */
export const normalizeScoringExplanationPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  if (Array.isArray(payload)) return payload;

  const nested =
    payload.data ??
    payload.result ??
    payload.body ??
    payload.response ??
    payload.scoring_explanation ??
    payload.scoringExplanation;

  if (nested && typeof nested === "object") {
    return { ...payload, ...nested };
  }

  return payload;
};

const carrierCodesFromRow = (row) => {
  if (!row || typeof row !== "object") return [];
  return [
    row.carrier_code,
    row.carrierCode,
    row.carrier_tenant_id,
    row.carrierTenantId,
    row.code,
    row.rate_carrier_code,
    row.rateCarrierCode,
  ]
    .map(normalizeCarrierCode)
    .filter(Boolean);
};

const rowMatchesCarrier = (row, carrierCode) => {
  if (!carrierCode) return true;
  const code = normalizeCarrierCode(carrierCode);
  const codes = carrierCodesFromRow(row);
  if (codes.includes(code)) return true;
  return codes.some(
    (c) => c === code || c.endsWith(`_${code}`) || c.endsWith(`-${code}`) || code.endsWith(c)
  );
};

const scoringExplanationItems = (payload) => {
  const normalized = normalizeScoringExplanationPayload(payload);
  const items = normalized?.items;
  return Array.isArray(items) ? items : [];
};

/** Resolve the matching carrier row from scoring-explanation `items[]`. */
export const resolveCarrierExplanationItem = (payload, carrierCode) => {
  if (!payload) return null;
  const normalized = normalizeScoringExplanationPayload(payload);
  const items = scoringExplanationItems(normalized);

  if (carrierCode) {
    for (const item of items) {
      if (rowMatchesCarrier(item, carrierCode)) return item;
    }
  }

  if (items.length === 1) return items[0];
  return null;
};

const hasUsefulCarrierExplanation = (item) =>
  Boolean(
    item &&
      ((Array.isArray(item.kpis) && item.kpis.length) ||
        item.kpi_factor_detail ||
        item.kpiFactorDetail ||
        item.score_calculation ||
        item.scoreCalculation ||
        item.price_calculation ||
        item.priceCalculation ||
        item.cost != null ||
        item.price != null ||
        (item.metrics && Object.keys(item.metrics).length))
  );

export const isUsefulScoringExplanation = (payload, carrierCode) => {
  if (!payload || typeof payload !== "object") return false;
  const items = scoringExplanationItems(payload);
  if (!items.length) return false;
  if (!carrierCode) return items.some(hasUsefulCarrierExplanation);
  const item = resolveCarrierExplanationItem(payload, carrierCode);
  return hasUsefulCarrierExplanation(item);
};

const parseKpiFactorRaw = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return { name: "KPI factor", value: trimmed };
    }
  }
  return raw;
};

const rowFromTuple = (entry, index) => {
  if (!Array.isArray(entry)) return null;
  if (entry.length >= 4) {
    return {
      name: entry[0],
      weight: entry[1],
      value: entry[2],
      contribution: entry[3],
    };
  }
  if (entry.length === 3) {
    return { name: entry[0], weight: entry[1], value: entry[2] };
  }
  if (entry.length === 2) {
    return { name: entry[0], value: entry[1] };
  }
  return { name: `KPI ${index + 1}`, value: entry[0] };
};

const normalizeKpiFactorRows = (raw) => {
  const parsed = parseKpiFactorRaw(raw);
  if (parsed == null) return [];

  if (typeof parsed === "number" || typeof parsed === "boolean") {
    return [{ name: "KPI factor", value: parsed }];
  }

  if (Array.isArray(parsed)) {
    return parsed.flatMap((row, index) => {
      if (Array.isArray(row)) {
        const tuple = rowFromTuple(row, index);
        return tuple ? [tuple] : [];
      }
      if (row && typeof row === "object") {
        if (Array.isArray(row.factors)) return normalizeKpiFactorRows(row.factors);
        if (Array.isArray(row.kpi_factor)) return normalizeKpiFactorRows(row.kpi_factor);
        if (Array.isArray(row.kpiFactor)) return normalizeKpiFactorRows(row.kpiFactor);
        return [row];
      }
      if (row == null || row === "") return [];
      return [{ name: `KPI ${index + 1}`, value: row }];
    });
  }

  if (typeof parsed === "object") {
    if (Array.isArray(parsed.factors)) return normalizeKpiFactorRows(parsed.factors);
    if (Array.isArray(parsed.items)) {
      return parsed.items.flatMap((item) => normalizeKpiFactorRows(item));
    }

    return Object.entries(parsed).flatMap(([key, value]) => {
      if (isKpiFactorKey(key)) {
        return normalizeKpiFactorRows(value);
      }
      if (Array.isArray(value)) {
        return value.flatMap((entry, index) => {
          if (Array.isArray(entry)) {
            const tuple = rowFromTuple(entry, index);
            return tuple ? [tuple] : [];
          }
          if (entry && typeof entry === "object") {
            return [
              {
                name: entry.name || entry.factor || entry.kpi || key,
                ...entry,
              },
            ];
          }
          return [{ name: key, value: entry }];
        });
      }
      if (value && typeof value === "object") {
        return [
          {
            name: value.name || value.factor || value.kpi || key,
            ...value,
          },
        ];
      }
      return [{ name: key, value }];
    });
  }

  return [{ name: "KPI factor", value: parsed }];
};

const pickKpiFactorForCarrier = (raw, carrierCode) => {
  const parsed = parseKpiFactorRaw(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return parsed;
  }

  if (!carrierCode) return parsed;

  const code = normalizeCarrierCode(carrierCode);
  const direct =
    parsed[carrierCode] ??
    parsed[code] ??
    parsed[carrierCode?.toLowerCase()];

  if (direct != null) return direct;

  const carrierEntry = Object.entries(parsed).find(
    ([key]) => normalizeCarrierCode(key) === code
  );
  if (carrierEntry) return carrierEntry[1];

  if (Array.isArray(parsed.factors)) return parsed.factors;
  if (Array.isArray(parsed.items)) {
    const item = parsed.items.find((row) => rowMatchesCarrier(row, carrierCode));
    if (item) return item;
  }

  return parsed;
};

const deepFindKpiFactorValues = (node, carrierCode, depth = 0) => {
  if (!node || depth > 8) return [];

  const parsed = parseKpiFactorRaw(node);
  if (parsed == null) return [];

  if (typeof parsed !== "object") {
    return normalizeKpiFactorRows(parsed);
  }

  if (Array.isArray(parsed)) {
    const carrierItems = carrierCode
      ? parsed.filter((row) => rowMatchesCarrier(row, carrierCode))
      : parsed;

    const fromItems = carrierItems.flatMap((row) => {
      const direct = extractKpiFactorsFromSource(row, "");
      if (direct.length) return direct;
      return normalizeKpiFactorRows(row);
    });
    if (fromItems.length) return fromItems;

    return parsed.flatMap((row, index) => deepFindKpiFactorValues(row, carrierCode, depth + 1));
  }

  const rows = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (isKpiFactorKey(key)) {
      const picked = pickKpiFactorForCarrier(value, carrierCode);
      rows.push(...normalizeKpiFactorRows(picked));
      continue;
    }

    if (key === "items" && Array.isArray(value)) {
      const carrierItem = carrierCode
        ? value.find((row) => rowMatchesCarrier(row, carrierCode))
        : null;
      if (carrierItem) {
        rows.push(...extractKpiFactorsFromSource(carrierItem, carrierCode));
      }
      continue;
    }

    if (value && typeof value === "object") {
      rows.push(...deepFindKpiFactorValues(value, carrierCode, depth + 1));
    }
  }

  return rows;
};

const METADATA_KEYS = new Set([
  "selection_outcome_id",
  "selectionOutcomeId",
  "shipment_id",
  "shipmentId",
  "config_id",
  "configId",
  "items",
  "carriers",
  "results",
  "scores",
  "scoring_spec",
  "scoringSpec",
]);

const SUMMARY_LABELS = new Set([
  "Rank",
  "Candidate rank",
  "Price",
  "Cost",
  "KPI-Cost",
  "KPI factor",
  "Score",
  "Total score",
]);

const KPI_SCORE_SOURCE_KEYS = [
  "kpi_scores",
  "kpiScores",
  "individual_kpi_scores",
  "individualKpiScores",
  "kpi_score_breakdown",
  "kpiScoreBreakdown",
  "factor_scores",
  "factorScores",
  "scoring_breakdown",
  "scoringBreakdown",
  "components",
  "breakdown",
];

const KPI_KEY_LABELS = {
  on_time: "On-Time Performance",
  on_time_performance: "On-Time Performance",
  acceptance: "Acceptance Rate",
  acceptance_rate: "Acceptance Rate",
  price: "Price Efficiency",
  price_efficiency: "Price Efficiency",
  claims_free: "Claims-Free Ratio",
  claims_free_ratio: "Claims-Free Ratio",
  capacity: "Capacity Match",
  capacity_match: "Capacity Match",
  transit: "Transit Time",
  transit_time: "Transit Time",
};

const isScalarKpiFactor = (value) =>
  value == null ||
  typeof value === "number" ||
  typeof value === "boolean" ||
  (typeof value === "string" && !value.trim().startsWith("{") && !value.trim().startsWith("["));

const humanizeKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isPlainScalar = (value) =>
  value == null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const labelForKpiKey = (key) => {
  const normalized = String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return KPI_KEY_LABELS[normalized] || humanizeKey(key);
};

const getScoringWeights = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  const spec = payload.scoring_spec ?? payload.scoringSpec;
  const weights = spec?.weights ?? spec;
  if (!weights || typeof weights !== "object" || Array.isArray(weights)) return {};
  return weights;
};

const weightFromSpec = (weights, key) => {
  if (!key || !weights || typeof weights !== "object") return undefined;
  const normalized = String(key)
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const raw =
    weights[key] ??
    weights[normalized] ??
    weights[key?.toLowerCase?.()] ??
    weights[labelForKpiKey(key)];
  if (raw == null) return undefined;
  const num = Number(raw);
  if (!Number.isFinite(num)) return raw;
  return num <= 1 ? num * 100 : num;
};

const normalizeKpiScoreEntries = (raw, weights = {}) => {
  if (raw == null || isScalarKpiFactor(raw)) return [];

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const entries = Object.entries(raw);
    const looksLikeScoreMap = entries.every(
      ([, value]) => isPlainScalar(value) || (value && typeof value === "object")
    );
    if (looksLikeScoreMap && !Array.isArray(raw.factors) && !Array.isArray(raw.items)) {
      return entries.flatMap(([key, value]) => {
        if (METADATA_KEYS.has(key) || isKpiFactorKey(key)) return [];
        if (value == null) return [];
        if (typeof value === "object" && !Array.isArray(value)) {
          return [
            {
              name:
                value.name ||
                value.kpi ||
                value.factor ||
                value.kpi_name ||
                value.kpiName ||
                labelForKpiKey(key),
              weight:
                value.weight ??
                value.percent ??
                value.kpi_weight ??
                value.kpiWeight ??
                weightFromSpec(weights, key),
              score:
                value.score ??
                value.value ??
                value.normalized_score ??
                value.normalizedScore,
              contribution:
                value.contribution ??
                value.weighted_score ??
                value.weightedScore ??
                value.kpi_contribution ??
                value.kpiContribution,
            },
          ];
        }
        return [
          {
            name: labelForKpiKey(key),
            weight: weightFromSpec(weights, key),
            score: value,
          },
        ];
      });
    }
  }

  return normalizeKpiFactorRows(raw)
    .map((row) => ({
      ...row,
      name:
        KPI_KEY_LABELS[String(row.name || "").toLowerCase()] ||
        labelForKpiKey(row.name) ||
        row.name,
      score:
        row.score ??
        row.value ??
        row.normalized_score ??
        row.normalizedScore,
      weight:
        row.weight ??
        row.percent ??
        row.kpi_weight ??
        row.kpiWeight ??
        weightFromSpec(weights, row.name || row.kpi || row.factor),
    }))
    .filter((row) => row.name && !SUMMARY_LABELS.has(row.name));
};

const deepFindIndividualKpiScores = (node, carrierCode, weights = {}, depth = 0) => {
  if (!node || depth > 8) return [];
  const parsed = parseKpiFactorRaw(node);
  if (parsed == null) return [];

  if (Array.isArray(parsed)) {
    const carrierItems = carrierCode
      ? parsed.filter((row) => rowMatchesCarrier(row, carrierCode))
      : parsed;
    const fromCarrierItems = carrierItems.flatMap((row) =>
      extractIndividualKpiScoresFromSource(row, "", weights)
    );
    if (fromCarrierItems.length) return fromCarrierItems;
    return parsed.flatMap((row) =>
      deepFindIndividualKpiScores(row, carrierCode, weights, depth + 1)
    );
  }

  if (typeof parsed !== "object") return [];

  const rows = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (METADATA_KEYS.has(key)) continue;

    if (KPI_SCORE_SOURCE_KEYS.includes(key)) {
      rows.push(...normalizeKpiScoreEntries(value, weights));
      continue;
    }

    if (isKpiFactorKey(key) && !isScalarKpiFactor(value)) {
      const picked = pickKpiFactorForCarrier(value, carrierCode);
      rows.push(...normalizeKpiScoreEntries(picked, weights));
      continue;
    }

    if (key === "items" && Array.isArray(value)) {
      const carrierItem = carrierCode
        ? value.find((row) => rowMatchesCarrier(row, carrierCode))
        : null;
      if (carrierItem) {
        rows.push(...extractIndividualKpiScoresFromSource(carrierItem, carrierCode, weights));
      }
      continue;
    }

    if (value && typeof value === "object") {
      rows.push(...deepFindIndividualKpiScores(value, carrierCode, weights, depth + 1));
    }
  }

  return rows;
};

const KPI_FIELD_PATTERN =
  /(?:^|_)(kpi|on_time|acceptance|price|claims|capacity|transit|factor|score)/i;

const SKIP_KPI_SCAN_KEYS = new Set([
  "carrier_code",
  "carrierCode",
  "code",
  "rank",
  "cost",
  "price",
  "carrier_rank",
  "carrierRank",
  "rate_carrier_code",
  "rateCarrierCode",
  "total_score",
  "totalScore",
  // Structured explanation/allocation objects, not individual KPI score maps - each of these
  // has its own dedicated extraction (kpiFactorStepsFromCarrierItem / kpiRowsFromCarrierItem).
  // Without this exclusion, KPI_FIELD_PATTERN's loose "factor"/"capacity" matching flattens their
  // nested keys (formula, steps, total_weighted_score, ...) into bogus per-row "KPI" entries.
  "kpi_factor_detail",
  "kpiFactorDetail",
  "excluded_no_capacity",
  "excludedNoCapacity",
]);

const scanRecordForKpiScores = (record, weights = {}) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return [];

  const rows = [];
  for (const [key, value] of Object.entries(record)) {
    if (METADATA_KEYS.has(key) || isKpiFactorKey(key) || SKIP_KPI_SCAN_KEYS.has(key)) {
      continue;
    }
    if (!KPI_FIELD_PATTERN.test(key)) continue;
    if (value == null || value === "") continue;

    const labelKey = String(key)
      .replace(/_score(s)?$/i, "")
      .replace(/_value(s)?$/i, "")
      .replace(/_pct$/i, "");

    if (isPlainScalar(value)) {
      rows.push({
        name: labelForKpiKey(labelKey),
        weight: weightFromSpec(weights, key),
        score: value,
      });
      continue;
    }

    if (typeof value === "object") {
      rows.push(
        ...normalizeKpiScoreEntries(value, weights).map((entry) => ({
          ...entry,
          name: entry.name || labelForKpiKey(labelKey),
        }))
      );
    }
  }

  return rows;
};

const extractIndividualKpiScoresFromSource = (source, carrierCode = "", weights = {}) => {
  if (!source || typeof source !== "object") return [];

  const rows = [];
  for (const key of KPI_SCORE_SOURCE_KEYS) {
    if (source[key] != null) {
      rows.push(...normalizeKpiScoreEntries(source[key], weights));
    }
  }

  for (const key of ["kpi_factor", "kpiFactor", "kpi_factors", "kpiFactors"]) {
    if (source[key] == null || isScalarKpiFactor(source[key])) continue;
    const picked = pickKpiFactorForCarrier(source[key], carrierCode);
    rows.push(...normalizeKpiScoreEntries(picked, weights));
  }

  for (const key of ["factors", "scores"]) {
    if (Array.isArray(source[key]) && source[key].length) {
      rows.push(...normalizeKpiScoreEntries(source[key], weights));
    }
  }

  rows.push(...scanRecordForKpiScores(source, weights));
  return rows;
};

const dedupeScoreRows = (rows) => {
  const seen = new Set();
  return rows.filter((row, index) => {
    const label =
      row?.name ||
      row?.factor ||
      row?.kpi ||
      row?.kpi_name ||
      row?.kpiName ||
      `kpi-${index}`;
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};

const FIELD_LABELS = {
  kpi_factor: "KPI factor",
  kpiFactor: "KPI factor",
  kpi_factor_name: "KPI factor",
  kpiFactorName: "KPI factor",
  factor_name: "KPI factor",
  factorName: "KPI factor",
  rank: "Rank",
  carrier_rank: "Rank",
  carrierRank: "Rank",
  cost: "Price",
  price: "KPI-Cost",
  score: "Score",
  total_score: "Total score",
  totalScore: "Total score",
  weight: "Weight",
  contribution: "Contribution",
  kpi_weight: "Weight",
  kpiWeight: "Weight",
  kpi_contribution: "Contribution",
  kpiContribution: "Contribution",
};

const rowsFromCarrierRecord = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return [];

  const rows = [];
  const factorName =
    row.kpi_factor_name ??
    row.kpiFactorName ??
    row.factor_name ??
    row.factorName ??
    row.kpi_name ??
    row.kpiName;

  const rawKpiFactor = row.kpi_factor ?? row.kpiFactor;
  if (rawKpiFactor != null && !isScalarKpiFactor(rawKpiFactor)) {
    rows.push(
      ...normalizeKpiScoreEntries(rawKpiFactor, getScoringWeights(row)).map((entry) => ({
        ...entry,
        name: entry.name || factorName || "KPI factor",
      }))
    );
  } else if (rawKpiFactor != null && factorName) {
    rows.push({
      name: factorName,
      weight: row.weight ?? row.kpi_weight ?? row.kpiWeight ?? row.percent,
      score: rawKpiFactor,
      contribution: row.contribution ?? row.kpi_contribution ?? row.kpiContribution,
    });
  }

  for (const key of ["kpi_factors", "kpiFactors", "factors", "scores"]) {
    if (Array.isArray(row[key]) && row[key].length) {
      rows.push(...normalizeKpiFactorRows(row[key]));
    }
  }

  for (const [key, value] of Object.entries(row)) {
    if (METADATA_KEYS.has(key) || isKpiFactorKey(key)) continue;
    if (!isPlainScalar(value)) continue;
    if (["kpi_factor", "kpiFactor", "weight", "contribution", "percent"].includes(key)) {
      continue;
    }
    rows.push({
      name: FIELD_LABELS[key] || humanizeKey(key),
      value,
    });
  }

  return rows;
};

const rowsFromRankedCarrierSummary = (row) => {
  if (!row || typeof row !== "object") return [];

  const rows = [];
  const add = (name, value) => {
    if (value == null || value === "") return;
    rows.push({ name, value });
  };

  add("Rank", row.rank);
  add("Price", row.cost);
  add("KPI-Cost", row.price);
  return rows;
};

const extractKpiFactorsFromSource = (source, carrierCode = "", { includeRoot = true } = {}) => {
  if (!source || typeof source !== "object") return [];

  if (!includeRoot && carrierCode) {
    return deepFindKpiFactorValues(source, carrierCode);
  }

  const rawKeys = ["kpi_factor", "kpiFactor", "kpi_factors", "kpiFactors"];
  for (const key of rawKeys) {
    if (source[key] == null || isScalarKpiFactor(source[key])) continue;
    const picked = pickKpiFactorForCarrier(source[key], carrierCode);
    const rows = normalizeKpiScoreEntries(picked, getScoringWeights(source));
    if (rows.length) return rows;
  }

  return deepFindKpiFactorValues(source, carrierCode).filter(
    (row) => row?.name && !SUMMARY_LABELS.has(row.name)
  );
};

/** Pull carrier-specific scoring from a cached scoring-explanation payload. */
export const findCarrierScoringExplanation = (payload, carrierCode) => {
  if (!payload || !carrierCode) return null;
  const normalized = normalizeScoringExplanationPayload(payload);

  const listKeys = [
    "items",
    "carriers",
    "carrier_scores",
    "carrierScores",
    "ranked_carriers",
    "rankedCarriers",
    "explanations",
    "scores",
    "results",
  ];

  for (const key of listKeys) {
    const list = normalized[key];
    if (!Array.isArray(list)) continue;
    const found = list.find((row) => rowMatchesCarrier(row, carrierCode));
    if (found) return found;
  }

  return null;
};

export const extractKpiFactors = (source, carrierCode = "") =>
  extractKpiFactorsFromSource(normalizeScoringExplanationPayload(source), carrierCode);

/** Individual KPI score rows (On-Time, Price Efficiency, etc.) for a carrier. */
export const collectIndividualKpiScores = (payload, carrierCode, ...fallbackSources) => {
  const normalized = normalizeScoringExplanationPayload(payload) ?? {};
  const weights = getScoringWeights(normalized);
  const merged = [];

  const carrierRow = resolveCarrierExplanationItem(payload, carrierCode);

  if (carrierRow) {
    merged.push(...kpiRowsFromCarrierItem(carrierRow));
    merged.push(...extractIndividualKpiScoresFromSource(carrierRow, carrierCode, weights));
    merged.push(...rowsFromCarrierRecord(carrierRow));
    merged.push(
      ...extractKpiFactorsFromSource(carrierRow, carrierCode, { includeRoot: true }).map(
        (row) => ({
          ...row,
          score: row.score ?? row.value,
        })
      )
    );
  }

  if (Array.isArray(normalized.items) && normalized.items.length) {
    for (const item of normalized.items) {
      if (item === carrierRow) continue;
      const itemCarriers = carrierCodesFromRow(item);
      if (
        carrierCode &&
        itemCarriers.length &&
        !rowMatchesCarrier(item, carrierCode)
      ) {
        continue;
      }
      merged.push(...rowsFromCarrierRecord(item));
      merged.push(...extractIndividualKpiScoresFromSource(item, carrierCode, weights));
    }
  }

  for (const fallback of fallbackSources.filter(Boolean)) {
    merged.push(...extractIndividualKpiScoresFromSource(fallback, carrierCode, weights));
  }

  if (!merged.length && Object.keys(normalized).length) {
    merged.push(...deepFindIndividualKpiScores(normalized, carrierCode, weights));
    merged.push(
      ...extractKpiFactorsFromSource(normalized, carrierCode, { includeRoot: false }).map(
        (row) => ({
          ...row,
          score: row.score ?? row.value,
        })
      )
    );
  }

  return dedupeScoreRows(
    merged.filter((row) => {
      const label = row?.name || row?.factor || row?.kpi;
      if (!label || SUMMARY_LABELS.has(label)) return false;
      return (
        row.score != null ||
        row.value != null ||
        row.contribution != null ||
        row.weight != null
      );
    })
  );
};

const extractOverallKpiFactor = (normalized, carrierCode, ...fallbackSources) => {
  const carrierRow = carrierCode
    ? findCarrierScoringExplanation(normalized, carrierCode)
    : null;

  const sources = [carrierRow, normalized, ...fallbackSources.filter(Boolean)].filter(Boolean);

  for (const source of sources) {
    const raw = source.kpi_factor ?? source.kpiFactor;
    if (raw == null || raw === "") continue;
    if (isScalarKpiFactor(raw)) return raw;
    if (carrierCode) {
      const picked = pickKpiFactorForCarrier(raw, carrierCode);
      if (isScalarKpiFactor(picked)) return picked;
    }
  }

  return null;
};

const costFromCarrierItem = (item) => {
  if (!item) return null;
  if (item.cost != null) return item.cost;
  const fromMetrics = item.metrics?.cost ?? item.metrics?.Cost;
  if (fromMetrics != null) return fromMetrics;
  const costKpi = (item.kpis || []).find(
    (k) => (k.metric_name || k.metricName || "").toLowerCase() === "cost"
  );
  return costKpi?.value ?? null;
};

/** Summary rows from scoring-explanation item (RankCost rank, price, KPI factor). */
const summaryRowsFromCarrierItem = (item, rankedRow) => {
  if (!item) return [];
  const rows = [];
  const add = (name, value) => {
    if (value == null || value === "") return;
    rows.push({ name, value });
  };

  const rank = item.rank ?? rankedRow?.rank;
  const candidateRank = item.candidate_rank ?? item.candidateRank;

  add("Rank", rank ?? candidateRank);
  if (candidateRank != null && rank != null && Number(candidateRank) !== Number(rank)) {
    add("Candidate rank", candidateRank);
  }
  add("Price", item.cost ?? costFromCarrierItem(item) ?? rankedRow?.cost);
  add("KPI factor", item.kpi_factor ?? item.kpiFactor);
  const kpiFactorAtSelection =
    item.kpi_factor_at_selection ?? item.kpiFactorAtSelection;
  if (
    kpiFactorAtSelection != null &&
    kpiFactorAtSelection !== "" &&
    String(kpiFactorAtSelection) !== String(item.kpi_factor ?? item.kpiFactor ?? "")
  ) {
    add("KPI factor at selection", kpiFactorAtSelection);
  }
  add("KPI-Cost", item.price ?? rankedRow?.price);
  return rows;
};

/** All KPI metric rows from enhanced API `kpis` array (includes cost). */
const kpiRowsFromCarrierItem = (item) => {
  if (!item) return [];

  const detail = item.kpi_factor_detail ?? item.kpiFactorDetail ?? {};
  const weightByKey = detail.weights_used ?? detail.weightsUsed ?? {};
  const steps = detail.steps ?? [];
  const stepByName = Object.fromEntries(
    steps.map((s) => [String(s.kpi_name ?? s.kpiName ?? "").toLowerCase(), s])
  );

  if (Array.isArray(item.kpis) && item.kpis.length) {
    return item.kpis
      .filter((k) => {
        const key = (k.metric_name ?? k.metricName ?? "").toLowerCase();
        return Boolean(key);
      })
      .map((k) => {
        const key = (k.metric_name ?? k.metricName ?? "").toLowerCase();
        const step = stepByName[key];
        const isCost = key === "cost";
        const penaltyMultiplier = isCost ? null : k.value;
        const onTimeRate = k.on_time_rate ?? k.onTimeRate;
        return {
          name: labelForKpiKey(k.metric_name ?? k.metricName),
          score: isCost ? k.value : (onTimeRate ?? k.value),
          penaltyMultiplier,
          onTimeRate,
          source: k.source,
          weight: step?.weight ?? weightByKey[key] ?? weightByKey[k.metric_name ?? k.metricName],
          contribution: step?.weighted_contribution ?? step?.weightedContribution,
          usedInKpiFactor: k.used_in_kpi_factor ?? k.usedInKpiFactor,
        };
      });
  }

  if (item.metrics && typeof item.metrics === "object" && !Array.isArray(item.metrics)) {
    return Object.entries(item.metrics)
      .filter(([key]) => key && key.toLowerCase() !== "sample_size")
      .map(([key, value]) => ({
        name: labelForKpiKey(key),
        score: value,
        source: "metrics",
      }));
  }

  return [];
};

/** KPI-factor calculation from `kpi_factor_detail` (Σ weight × penalty_multiplier). */
const kpiFactorStepsFromCarrierItem = (item) => {
  const detail = item?.kpi_factor_detail ?? item?.kpiFactorDetail;
  if (!detail) return { formula: null, kpiFactor: null, steps: [] };

  const nullReason = detail.null_reason ?? detail.nullReason;
  // No matching config (or a config with no scoring_spec weights) - nothing was actually
  // configured to score against, so don't show a KPI factor breakdown at all rather than a
  // formula/steps table for a factor that only exists because of a hardcoded fallback.
  if (nullReason === "NO_KPI_WEIGHTS") {
    return { formula: null, kpiFactor: null, steps: [], nullReason };
  }

  const penaltyByKey = detail.penalty_multipliers ?? detail.penaltyMultipliers ?? {};
  const steps = (detail.steps ?? []).map((s) => {
    const key = (s.kpi_name ?? s.kpiName ?? "").toLowerCase();
    const penaltyMultiplier =
      s.penalty_multiplier ??
      s.penaltyMultiplier ??
      penaltyByKey[key] ??
      penaltyByKey[s.kpi_name ?? s.kpiName] ??
      s.raw_score ??
      s.rawScore;
    return {
      name: labelForKpiKey(s.kpi_name ?? s.kpiName),
      weight: s.weight,
      score: penaltyMultiplier,
      penaltyMultiplier,
      contribution: s.weighted_contribution ?? s.weightedContribution,
      calculation: s.calculation,
    };
  });

  const kpiFactor =
    detail.kpi_factor ??
    detail.kpiFactor ??
    detail.total_weighted_score ??
    detail.totalWeightedScore;

  return {
    formula: detail.formula,
    kpiFactor,
    penaltyMultipliers: detail.penalty_multipliers ?? detail.penaltyMultipliers,
    nullReason: detail.null_reason ?? detail.nullReason,
    steps,
  };
};

/** RankCost price calculation: price = cost × kpi_factor. */
const priceCalculationFromCarrierItem = (item) => {
  const calc = item?.price_calculation ?? item?.priceCalculation;
  if (!calc) return null;

  return {
    formula: calc.formula,
    cost: calc.cost,
    kpiFactor: calc.kpi_factor ?? calc.kpiFactor,
    price: calc.price,
  };
};

const kpiRowsFromKpiFactorSteps = (item) => {
  const { steps } = kpiFactorStepsFromCarrierItem(item);
  return steps.map((s) => ({
    name: s.name,
    weight: s.weight,
    score: s.score,
    contribution: s.contribution,
  }));
};

/** Rank / cost / KPI-cost summary rows for a carrier. */
export const collectCarrierScoreSummary = (payload, carrierCode, ...fallbackSources) => {
  const normalized = normalizeScoringExplanationPayload(payload) ?? {};
  const rankedRow = fallbackSources.find(Boolean);
  const carrierRow = resolveCarrierExplanationItem(payload, carrierCode);

  if (carrierRow && hasUsefulCarrierExplanation(carrierRow)) {
    return dedupeScoreRows(summaryRowsFromCarrierItem(carrierRow, rankedRow));
  }

  const merged = [];

  if (carrierRow) {
    merged.push(...rowsFromRankedCarrierSummary(carrierRow));
    for (const [key, value] of Object.entries(carrierRow)) {
      if (!isPlainScalar(value)) continue;
      if (METADATA_KEYS.has(key) || isKpiFactorKey(key)) continue;
      if (["rank", "cost", "price", "carrier_code", "carrierCode", "code", "sample_size", "sampleSize"].includes(key)) {
        continue;
      }
      merged.push({
        name: FIELD_LABELS[key] || humanizeKey(key),
        value,
      });
    }
  }

  for (const fallback of fallbackSources.filter(Boolean)) {
    merged.push(...rowsFromRankedCarrierSummary(fallback));
  }

  const overallKpiFactor = extractOverallKpiFactor(
    normalized,
    carrierCode,
    ...fallbackSources
  );
  if (overallKpiFactor != null && overallKpiFactor !== "") {
    merged.push({ name: "KPI factor", value: overallKpiFactor });
  }

  return dedupeScoreRows(
    merged.filter((row) => row?.name && (row.value != null || row.score != null))
  );
};

/** True when a row already looks like a single carrier item (has its own `kpis`/`kpi_factor_detail`
 * fields directly on it), e.g. a raw ranked-carrier row from the live rate-shopping response —
 * as opposed to an envelope that needs unwrapping via `resolveCarrierExplanationItem`. */
const looksLikeCarrierItem = (row) =>
  Boolean(
    row &&
      typeof row === "object" &&
      ((Array.isArray(row.kpis) && row.kpis.length) || row.kpi_factor_detail || row.kpiFactorDetail)
  );

/** Collect summary + individual KPI scores for score-details UI. */
export const collectCarrierScoringBreakdown = (payload, carrierCode, ...fallbackSources) => {
  const normalized = normalizeScoringExplanationPayload(payload) ?? {};
  const rankedRow = fallbackSources.find(Boolean);
  const resolvedRow = resolveCarrierExplanationItem(payload, carrierCode);
  // Prefer a wrapped explanation item, but fall back to the raw ranked-carrier row itself when it
  // already carries kpis/kpi_factor_detail directly - avoids the generic key-pattern scan (which
  // can't tell a KPI score map from a structured explanation object) for data we already have in
  // the right shape.
  const carrierRow = resolvedRow || (looksLikeCarrierItem(rankedRow) ? rankedRow : null);

  const kpiScoreRowsFromApi = kpiRowsFromCarrierItem(carrierRow);
  const kpiFactorSteps = carrierRow ? kpiFactorStepsFromCarrierItem(carrierRow) : null;
  const priceCalculation = carrierRow ? priceCalculationFromCarrierItem(carrierRow) : null;

  const kpiScoreRows = kpiScoreRowsFromApi.length
    ? kpiScoreRowsFromApi
    : carrierRow
      ? kpiRowsFromKpiFactorSteps(carrierRow)
      : payload || rankedRow
        ? collectIndividualKpiScores(payload ?? rankedRow, carrierCode, ...fallbackSources)
        : [];

  return {
    summaryRows: collectCarrierScoreSummary(payload, carrierCode, ...fallbackSources),
    kpiScoreRows,
    kpiFactorCalculation: kpiFactorSteps,
    priceCalculation,
    rankingFormula: normalized.ranking_formula ?? normalized.rankingFormula ?? null,
  };
};

/** @deprecated Use collectCarrierScoringBreakdown or collectIndividualKpiScores. */
export const collectCarrierKpiFactors = (payload, carrierCode, ...fallbackSources) => {
  const { summaryRows, kpiScoreRows } = collectCarrierScoringBreakdown(
    payload,
    carrierCode,
    ...fallbackSources
  );
  return dedupeScoreRows([...summaryRows, ...kpiScoreRows]);
};

export const extractSelectionOutcomeId = (data) => {
  if (!data || typeof data !== "object") return null;
  const candidates = [
    data.selection_outcome_id,
    data.selectionOutcomeId,
    data.outcome_id,
    data.outcomeId,
    data.selection_outcome?.id,
    data.selection_outcome?.outcome_id,
    data.selection_outcome?.selection_outcome_id,
    data.selectionOutcome?.id,
    data.selectionOutcome?.outcomeId,
    data.selectionOutcome?.selectionOutcomeId,
    data.selection?.selection_outcome_id,
    data.selection?.selectionOutcomeId,
    data.selection?.outcome_id,
    data.selection?.outcomeId,
    data.selection?.id,
    data.outcome?.id,
    data.outcome?.outcome_id,
  ];
  for (const value of candidates) {
    if (value != null && value !== "") return String(value);
  }

  const ranked = data.ranked_carriers || data.rankedCarriers;
  if (Array.isArray(ranked)) {
    for (const row of ranked) {
      const fromRow = extractSelectionOutcomeId(row);
      if (fromRow) return fromRow;
    }
  }

  return null;
};

export const pickLatestSelectionOutcomeId = (data) => {
  if (!data) return null;
  const direct = extractSelectionOutcomeId(data);
  if (direct) return direct;

  const list = Array.isArray(data)
    ? data
    : data.items ||
      data.content ||
      data.results ||
      data.selection_outcomes ||
      data.selectionOutcomes ||
      [];

  if (!Array.isArray(list) || !list.length) return null;

  const sorted = [...list].sort((a, b) => {
    const aTime = Date.parse(a?.created_at || a?.createdAt || a?.timestamp || 0);
    const bTime = Date.parse(b?.created_at || b?.createdAt || b?.timestamp || 0);
    return bTime - aTime;
  });

  for (const row of sorted) {
    const id =
      row?.selection_outcome_id ??
      row?.selectionOutcomeId ??
      row?.outcome_id ??
      row?.outcomeId ??
      row?.id;
    if (id != null && id !== "") return String(id);
  }

  return null;
};
